import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Spinner } from '../design-system';

type Column = 'per_city_trips' | 'per_city_trips_admins';

/**
 * The switches for trips with several cities. They save on their own, apart from
 * the price form, so saving prices can never flip them.
 *
 * - "Admins only (test)": several-city trips for admin accounts only, so an admin
 *   can plan and pay a real test-mode trip with two cities while travelers see
 *   nothing. On and off are immediate.
 * - "Everyone": on for all travelers. Turning it on asks for a confirmation;
 *   turning it off is immediate and leaves booked trips working.
 */
export function MultiCitySwitch({
  settingsId,
  initial,
  initialAdmins = false,
}: {
  settingsId: string;
  initial: boolean;
  initialAdmins?: boolean;
}) {
  const [on, setOn] = useState(initial);
  const [adminsOn, setAdminsOn] = useState(initialAdmins);
  const [confirming, setConfirming] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [saving, setSaving] = useState<Column | null>(null);
  const [error, setError] = useState(false);

  async function save(column: Column, next: boolean) {
    setSaving(column);
    setError(false);
    const { error: err } = await supabase
      .from('platform_settings')
      .update({ [column]: next, updated_at: new Date().toISOString() })
      .eq('id', settingsId);
    setSaving(null);
    if (err) {
      console.error(`${column} save failed:`, err);
      setError(true);
      return;
    }
    if (column === 'per_city_trips') {
      setOn(next);
      setConfirming(false);
      setUnderstood(false);
    } else {
      setAdminsOn(next);
    }
  }

  const status = on ? 'On' : adminsOn ? 'Admins only' : 'Off';

  return (
    <div className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-app-fg uppercase tracking-wide">Trips with several cities</h2>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            on ? 'bg-emerald-500/15 text-emerald-500' : adminsOn ? 'bg-gold/15 text-gold' : 'bg-app-border text-app-muted'
          }`}
        >
          {status}
        </span>
      </div>
      <p className="text-xs text-app-muted">
        {on
          ? 'Travelers can pick several cities. Each city has its own dates, price, hosts to choose from and payment.'
          : adminsOn
            ? 'Only admin accounts get trips with several cities, to test them. Travelers book one stay at a time and see nothing of it.'
            : 'Travelers book one stay at a time. Nothing about several cities is visible to them.'}
      </p>

      {!on && (
        <div className="rounded-lg border border-app-border p-4 flex flex-col gap-2">
          <p className="text-sm font-medium text-app-fg">1. Test with admin accounts</p>
          <p className="text-xs text-app-muted">
            {adminsOn
              ? 'On. With an admin account, plan a trip with two cities in the app, choose a host for each city and pay with a Stripe test card. Travelers are not affected.'
              : 'Turns several-city trips on for admin accounts only, so you can try a real test-mode booking first. Travelers are not affected.'}
          </p>
          <div>
            <Button
              variant="secondary"
              onClick={() => void save('per_city_trips_admins', !adminsOn)}
              disabled={saving !== null}
            >
              {saving === 'per_city_trips_admins' ? <Spinner size="sm" /> : adminsOn ? 'Stop the test' : 'Test with admins'}
            </Button>
          </div>
        </div>
      )}

      {!on && !confirming && (
        <div className="rounded-lg border border-app-border p-4 flex flex-col gap-2">
          <p className="text-sm font-medium text-app-fg">2. Turn on for everyone</p>
          <div>
            <Button variant="secondary" onClick={() => setConfirming(true)} disabled={saving !== null}>Turn on for everyone…</Button>
          </div>
        </div>
      )}

      {!on && confirming && (
        <div role="alert" className="rounded-lg border border-gold/30 bg-gold/10 p-4 flex flex-col gap-3 text-sm text-app-fg">
          <p>
            This changes what every traveler sees and pays: several cities in one trip, one payment per city, one chat per
            host. Turn it on once a test-mode payment for two cities worked with an admin account (step 1), and every city
            you offer has enough hosts.
          </p>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} className="mt-1" />
            <span>I tested a multi-city payment with an admin account and want to turn this on for all travelers.</span>
          </label>
          <div className="flex gap-2">
            <Button onClick={() => void save('per_city_trips', true)} disabled={!understood || saving !== null}>
              {saving === 'per_city_trips' ? <Spinner size="sm" className="text-ink" /> : 'Turn on'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => { setConfirming(false); setUnderstood(false); }}
              disabled={saving !== null}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {on && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-app-muted">Turning it off stops new multi-city bookings. Trips already booked keep working.</p>
          <div>
            <Button variant="secondary" onClick={() => void save('per_city_trips', false)} disabled={saving !== null}>
              {saving === 'per_city_trips' ? <Spinner size="sm" /> : 'Turn off'}
            </Button>
          </div>
        </div>
      )}

      {error && <p role="alert" className="text-sm text-crimson">We couldn't save this. Try again.</p>}
    </div>
  );
}
